import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { NgAsModule } from "ng-as";
import { ConfirmationService } from "primeng/api";
import { ChipModule } from "primeng/chip";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from "primeng/fileupload";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SidebarModule } from "primeng/sidebar";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { AbstractPaginableComponent } from "src/app/common/components/paginable/paginable.component";
import { AclService } from "src/app/common/services/acl.service";
import { FileContentService } from "src/app/common/services/file-content.service";
import { GoogleCloudService } from "src/app/common/services/google-cloud.service";
import { NotificationService } from "src/app/common/services/notification.service";
import { UserTrackingService } from "src/app/common/services/user-tracking.service";
import { fileContentExtensions } from "src/app/config";

export type FileContent = {
  id: string;
  filename: string;
  uploadDate: Date;
  content?: string;
  filepath?: string;
};

export type FileInfo = {
  id: string;
  filename: string;
  filepath?: string;
};

@Component({
  selector: "app-file-content-list",
  standalone: true,
  imports: [
    ProgressSpinnerModule,
    CommonModule,
    NgAsModule,
    TableModule,
    ConfirmDialogModule,
    SidebarModule,
    FileUploadModule,
    TooltipModule,
    ChipModule,
  ],
  templateUrl: "./file-content-list.component.html",
  styleUrl: "./file-content-list.component.scss",
})
export class FileContentListComponent extends AbstractPaginableComponent implements OnInit {
  validExtensions = fileContentExtensions;
  uploading = false;
  selectedFiles: FileContent[] = [];
  sidebarVisible = false;
  values: string[] | undefined;
  fileContentList: FileContent[] = [{} as FileContent];
  FileContent!: FileContent;
  username!: string;
  @ViewChild("fileUpload", { static: false }) fileUpload!: FileUpload;
  MAX_FILE_SIZE = 20 * 1024 * 1024; //20MB
  selectionMode = false; //whether the dialog is used to select a file content or not
  selectedForSideBar: FileContent | undefined;
  selectedForSideBarNumberOfChunks: number | undefined;
  onlyWithFilePath = false;
  private readonly confirmationService = inject(ConfirmationService);
  private readonly aclService = inject(AclService);
  private readonly msalService = inject(MsalService);
  private readonly notificationService = inject(NotificationService);
  private readonly userTrackingService = inject(UserTrackingService);

  private readonly fileContentService = inject(FileContentService);
  private readonly googleCloudService = inject(GoogleCloudService);
  protected readonly ref = inject(DynamicDialogRef);
  private readonly dialogConfig: DynamicDialogConfig<{
    selectionMode: boolean;
    onlyWithFilePath?: boolean;
  }> = inject(DynamicDialogConfig);

  async ngOnInit(): Promise<void> {
    this.selectionMode = this.dialogConfig.data!.selectionMode;
    this.onlyWithFilePath = this.dialogConfig.data!.onlyWithFilePath || false;
    this.username = this.msalService.instance.getAllAccounts()[0]!.name!;
    await this.loadItems(0);
  }

  async showFileContent(fileContent: FileContent): Promise<void> {
    this.sidebarVisible = true;
    this.selectedForSideBar = fileContent;
    if (!this.selectedForSideBar.content) {
      this.selectedForSideBar.content = await this.fileContentService.getContent(fileContent.id);
    }
    this.selectedForSideBarNumberOfChunks = await this.fileContentService.getNumberOfChunks(fileContent.id);
  }

  removeSelectedFile(id: string): void {
    this.selectedFiles = this.selectedFiles.filter((file) => file.id !== id);
  }

  addSelectedFile(file: FileContent): void {
    if (this.selectedFiles.find((f) => f.id === file.id)) {
      this.notificationService.showWarning(
        $localize`:@@file_content_list_already_selected_message:Ce fichier est déjà sélectionné`,
      );
    } else {
      this.selectedFiles.push(file);
      this.closeDialogWithSelectedFiles();
    }
  }

  override async loadItems(firstItemIndex: number): Promise<void> {
    this.fileContentList = [{} as FileContent];
    this.pageLoading = true;
    const page = Number(firstItemIndex / this.pageSize);
    const pagedFileContent = await this.fileContentService.fetchPage(page, this.pageSize);
    if (pagedFileContent) {
      this.pageLoading = false;
      this.fileContentList = pagedFileContent.elements ?? [];
      if (this.onlyWithFilePath) {
        this.fileContentList = this.fileContentList.filter((file) => file.filepath);
      }
      this.totalRecords = pagedFileContent.totalElements;
    }
  }

  /**
   * 1. Upload files to AWS S3 via fileContentService.
   * 2. Then upload the same files to Google Cloud using each file's .name.
   */
  async onUpload(event: FileUploadHandlerEvent): Promise<void> {
    this.uploading = true;
    try {
      // Upload to AWS S3 (existing logic)
      const responsePerFile = await this.fileContentService.upload(this.username, event.files);

      // Check S3 response, optionally handle partial failures
      for (const res of responsePerFile) {
        if (res.status !== "success") {
          const errorMessage = `Erreur d'upload du fichier: ${res.filename}  ${res.detail}`;
          this.notificationService.showWarning(errorMessage);
        }
      }

      // Upload the SAME files to Google Cloud
      for (const res of responsePerFile) {
        if (res.filepath) {
          try {
            // Call the new backend endpoint
            await this.googleCloudService.uploadFileFromS3(res.filepath);
          } catch (gcError) {
            console.error("Error uploading to Google Cloud:", res.filepath, gcError);
            this.notificationService.showWarning(`Erreur d'upload GCloud: ${res.filepath} => ${String(gcError)}`);
          }
        }
      }

      // Reload items in the table
      await this.loadItems(0);
    } catch (e) {
      this.notificationService.showError(e, "Erreur lors de l'upload");
    } finally {
      this.uploading = false;
      // Clear the file input
      this.fileUpload.clear();
      // Track user action
      await this.userTrackingService.track(this.username, "PROMPT_UPLOAD_FILE");
      // Close the dialog if in selection mode
      this.closeDialogWithSelectedFiles();
    }
  }

  /**
   * 1. Confirm deletion from AWS S3.
   * 2. Then delete from Google Cloud with the same "fileContent.filename".
   */
  deleteFileContent(fileContent: FileContent): void {
    this.confirmationService.confirm({
      message: $localize`:@@file_content_list_delete_confirmation_header:\
Êtes-vous sûr de vouloir supprimer ce fichier. \
Son contenu ne sera plus disponible si il utilisé dans un prompt ou un context de prompt ?`,
      header: $localize`:@@prompt_selector_delete_prompt_confirmation_title:Confirmation de suppression`,
      icon: "pi pi-info-circle",
      acceptButtonStyleClass: "p-button-danger p-button-text",
      rejectButtonStyleClass: "p-button-text p-button-text",
      acceptLabel: $localize`:@@prompt_selector_delete_prompt_accept_button:Oui`,
      rejectLabel: $localize`:@@prompt_selector_delete_prompt_reject_button:Non`,

      accept: async () => {
        // Delete from AWS S3
        await this.fileContentService.delete(fileContent.id);

        // Delete from Google Cloud
        try {
          await this.googleCloudService.deleteFile(fileContent.filename);
        } catch (gcError) {
          console.error("### Error deleting file from Google Cloud:", fileContent.filename, gcError);
          this.notificationService.showWarning(`Erreur de suppression GCloud: ${fileContent.filename}`);
        }

        // Refresh table
        await this.loadItems(0);
        this.confirmationService.close();
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  closeDialogWithSelectedFiles(): void {
    // only return the id and filename of the selected files
    this.ref.close(
      this.selectedFiles.map((file) => ({ id: file.id, filename: file.filename, filepath: file.filepath })),
    );
  }
}
